import { parseDbTimestamp } from "@repo/shared";
import type {
  AssessmentAttribution,
  LocalizedString,
  ProductAssessmentExperienceResult,
  ProductAssessmentQuizState,
  ProductAssessmentResult,
  ProductAssessmentStoredValue,
  ProductAssessmentSubjectInput,
  ProductTypeAssessmentMode,
  ProductTypeAssessmentPublic,
  StartQuizAttemptResponse,
  SubmitQuestionnaireRequest,
  SubmitQuizAttemptRequest,
  SubmitQuizAttemptResponse,
} from "@repo/shared/types";
import { History } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { ProductAssessmentQuestionnaireModal } from "../product-assessment-questionnaire-modal";
import { ProductAssessmentQuizModal } from "../product-assessment-quiz-modal";
import {
  RepresentativeNote,
  representativeNoteText,
  resolveAttributionBanner,
} from "../representative-note";
import { externalFor, findOutcome, resolveSubjectVerdict } from "./mode-row";
import { T } from "./product-assessment-widget.i18n";
import { ProductTypeBlock, type ProductTypeBlockRow } from "./product-type-block";

// Fixed sub-row order within a product type's block: quiz above questionnaire.
const MODE_ORDER: ProductTypeAssessmentMode[] = ["QUIZ", "QUESTIONNAIRE"];

type ProductType = {
  id: string;
  nameI18n: LocalizedString;
};

const startingKeyFor = (productTypeId: string, subjectId?: string) =>
  `${productTypeId}|${subjectId ?? ""}`;

const declarationRowKey = (typeId: string) => `${typeId}:declaration`;
const modeRowKey = (typeId: string, mode: ProductTypeAssessmentMode) => `${typeId}:${mode}`;

export type ProductAssessmentWidgetProps = {
  portfolioCollectionId: string;
  portfolioCollectionName: string;
  productTypes: ProductType[];
  inputData?: string | null;
  calculatedData?: string | null;
  /**
   * QL-194 — plain declaration for a declaration-only product type, persisted
   * per category via `POST …/declare`. The recomputed value flows back through
   * `inputData`; the widget only keeps an optimistic override until it settles.
   * QL-505 — `subjectId` addresses one party when several answer (a group
   * member, or — QL-654 — a representative when 2+ are registered); absent for
   * every other client.
   */
  onDeclare: (declarations: Record<string, boolean>, subjectId?: string) => Promise<unknown>;
  /** QL-194 — one category's questionnaire, sent whole on "Done". */
  onSubmitQuestionnaire: (
    request: SubmitQuestionnaireRequest,
    subjectId?: string,
  ) => Promise<unknown>;
  disabled?: boolean;
  // Product-type assessments — any number of enabled modes per product type
  // (at most one per mode). A product type with an assessment replaces the
  // legacy yes/no checkbox with an inline questionnaire (QUESTIONNAIRE) or a
  // quiz launcher (QUIZ); the category verdict is the AND across all modes.
  assessments?: ProductTypeAssessmentPublic[] | null;
  onStartQuiz?: (productTypeId: string, subjectId?: string) => Promise<StartQuizAttemptResponse>;
  onSubmitQuiz?: (
    request: SubmitQuizAttemptRequest,
    subjectId?: string,
  ) => Promise<SubmitQuizAttemptResponse>;
  // Product-type ids that have at least one product selected in the flow's
  // portfolios. A not-passed category whose products are actually in use shows
  // a blocking error (mirrors the custom-portfolio suitability gate).
  productTypeIdsInUse?: string[];
  /** QL-505 — server-decided attribution banner / per-member rendering. */
  attribution?: AssessmentAttribution;
  /**
   * QL-505 — set only on a member-scoped client session (a member link): the
   * one member line that session may act on. Every other member line is
   * read-only.
   */
  sessionSubjectId?: string | null;
  /** QL-505 — a group session viewing a members-mode group: every member line is read-only. */
  readOnlyMembers?: boolean;
  // Backend-sourced reuse, identical on both surfaces. `available`/`sourceDate` say whether a
  // previous answer exists; `onApply` calls the apply endpoint, which merges declarations,
  // questionnaire answers and quiz states server-side. The recomputed value flows back via
  // `inputData`.
  available?: boolean;
  sourceDate?: string | null;
  onApply?: () => Promise<void>;
  // Configurable styling props
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  hasValidationError?: boolean;
};

export function ProductAssessmentWidget({
  portfolioCollectionId: _portfolioCollectionId,
  portfolioCollectionName: _portfolioCollectionName,
  productTypes,
  inputData,
  calculatedData,
  onDeclare,
  onSubmitQuestionnaire,
  disabled = false,
  assessments,
  onStartQuiz,
  onSubmitQuiz,
  productTypeIdsInUse,
  attribution,
  sessionSubjectId = null,
  readOnlyMembers = false,
  available = false,
  sourceDate,
  onApply,
  style = "none",
  title,
  description,
  hasValidationError = false,
}: ProductAssessmentWidgetProps) {
  const localize = useLocalize();
  const { formatDate: formatRegionalDate } = useRegionalFormat();
  const t = useT(T);
  // QL-654 — 2+ representatives answer in the same list as a group's members,
  // headed "Representatives" instead of "Members".
  const isRepresentativeList =
    attribution?.kind === "members" && attribution.basis === "representatives";

  // The stored blob is server-authored (QL-194): declarations, questionnaire
  // answers and quiz states are derived from `inputData` — never edited locally.
  // Every user action is its own mutation and the recomputed value flows back
  // through `inputData`. QL-505 — `members` carries the same shape per member.
  const { experienceMap, questionnaireAnswers, quizStates, memberSubjects } = useMemo(() => {
    const empty = {
      experienceMap: {} as Record<string, boolean>,
      questionnaireAnswers: {} as Record<string, Record<string, string>>,
      quizStates: {} as Record<string, ProductAssessmentQuizState>,
      memberSubjects: {} as Record<string, ProductAssessmentSubjectInput>,
    };
    if (!inputData) return empty;
    try {
      const parsed = JSON.parse(inputData) as ProductAssessmentStoredValue;
      const experienceMap: Record<string, boolean> = {};
      for (const exp of parsed.experiences ?? []) {
        experienceMap[exp.productTypeId] = exp.hasExperience;
      }
      return {
        experienceMap,
        questionnaireAnswers: parsed.questionnaireAnswers ?? {},
        quizStates: parsed.quizStates ?? {},
        memberSubjects: parsed.members ?? {},
      };
    } catch (error) {
      log.error({ err: error }, "failed to parse product assessment value");
      return empty;
    }
  }, [inputData]);
  // Optimistic layer over experienceMap for declaration-only checkboxes: set on
  // click, dropped once the declare request settles (success → inputData has
  // the new value; error → the checkbox falls back to the stored value).
  const [declarationOverrides, setDeclarationOverrides] = useState<Record<string, boolean>>({});
  const [memberDeclarationOverrides, setMemberDeclarationOverrides] = useState<
    Record<string, Record<string, boolean>>
  >({});
  // "Start fresh" on the choice screen — a local decision only, nothing is written.
  const [hasDecided, setHasDecided] = useState(false);
  const [isApplyingPrevious, setIsApplyingPrevious] = useState(false);
  const [reuseError, setReuseError] = useState<string | null>(null);
  // Quiz runner state, keyed by productTypeId (+ member, for a group without a representative).
  const [activeQuiz, setActiveQuiz] = useState<{
    productTypeId: string;
    subjectId: string | null;
    label: string;
    attempt: StartQuizAttemptResponse;
  } | null>(null);
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [quizStartError, setQuizStartError] = useState<string | null>(null);
  // Questionnaire runner (keyed by productTypeId + member) — answered in a
  // modal so the page stays compact with many categories.
  const [activeQuestionnaire, setActiveQuestionnaire] = useState<{
    productTypeId: string;
    subjectId: string | null;
    label: string;
    assessment: ProductTypeAssessmentPublic;
  } | null>(null);
  // Which group mode-rows / declaration groups are expanded to show member lines.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpand = (key: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // All enabled assessment modes per product type, keyed by productTypeId, in
  // MODE_ORDER (quiz above questionnaire). Server ordering is unspecified, so
  // the sub-row order is pinned here.
  const assessmentsByType = useMemo(() => {
    const byType = new Map<string, ProductTypeAssessmentPublic[]>();
    for (const assessment of assessments ?? []) {
      const list = byType.get(assessment.productTypeId);
      if (list) list.push(assessment);
      else byType.set(assessment.productTypeId, [assessment]);
    }
    for (const list of byType.values()) {
      list.sort((a, b) => MODE_ORDER.indexOf(a.mode) - MODE_ORDER.indexOf(b.mode));
    }
    return byType;
  }, [assessments]);
  const inUseSet = useMemo(() => new Set(productTypeIdsInUse ?? []), [productTypeIdsInUse]);

  // Per-category outcomes from calculatedData (server verdicts).
  const calcResult = useMemo<ProductAssessmentResult | null>(() => {
    if (!calculatedData) return null;
    try {
      return JSON.parse(calculatedData) as ProductAssessmentResult;
    } catch (error) {
      log.error({ err: error }, "failed to parse product assessment calculated data");
      return null;
    }
  }, [calculatedData]);

  // QL-505 — a group without a representative: the top-level fields are the
  // AGGREGATE and every answering subject is a current member instead.
  // Decided from the server-resolved `attribution`, never from stored
  // calculatedData — a fresh instance has no calculatedData yet, so a
  // calcResult-based check would render a repless group as a single subject
  // and every action would then 400 (member_id_required).
  const isMembersMode = attribution?.kind === "members";
  // When nothing has been computed yet (fresh instance), fall back to the
  // current member list from `attribution` with empty per-member state —
  // Start quiz / Answer questions / unticked, exactly like an unanswered
  // single subject.
  // QL-654 — a line is addressed by its subject id; rows computed before
  // QL-654 carry only `memberId`, which holds the same id.
  // QL-660 — who answers for each member right now, from the server attribution.
  const representativeBySubject = new Map(
    attribution?.kind === "members"
      ? attribution.members.map((m) => [m.subjectId, m.memberRepresentative] as const)
      : [],
  );
  const members =
    calcResult?.memberResults?.map((m) => ({
      subjectId: m.subjectId ?? m.memberId,
      name: m.name,
      memberRepresentative: representativeBySubject.get(m.subjectId ?? m.memberId) ?? null,
      experiences: m.experiences,
    })) ??
    (attribution?.kind === "members"
      ? attribution.members.map((m) => ({
          subjectId: m.subjectId,
          name: m.name,
          memberRepresentative: m.memberRepresentative,
          experiences: [] as ProductAssessmentExperienceResult[],
        }))
      : []);

  const isMemberLink = sessionSubjectId != null;

  const isMemberActionable = (subjectId: string) => {
    if (disabled || readOnlyMembers) return false;
    if (sessionSubjectId != null) return sessionSubjectId === subjectId;
    return true;
  };

  // Spec UX — on a member link, every group row auto-expands to that member's
  // own line, and only that line shows (the aggregate count stays visible in
  // the row header, computed from the full group, untouched by this filter).
  function onlyMemberLink<M extends { subjectId: string }>(list: M[]): M[] {
    return isMemberLink ? list.filter((m) => m.subjectId === sessionSubjectId) : list;
  }

  // Reuse is always backend-resolved and backend-applied — both surfaces pass the same props.
  const canReuse = available && !!onApply;
  const reuseDate = sourceDate;

  // Spec UX — on a member link, "already answered" is decided from that
  // member's OWN subject, not the whole blob: `inputData` can be non-null
  // (another member already answered something) while this member has
  // answered nothing at all, which must still offer the choice screen.
  const memberSubjectIsEmpty = (subject: ProductAssessmentSubjectInput | undefined) => {
    if (!subject) return true;
    return (
      (subject.experiences?.length ?? 0) === 0 &&
      Object.keys(subject.questionnaireAnswers ?? {}).length === 0 &&
      Object.keys(subject.quizStates ?? {}).length === 0
    );
  };
  const hasOwnAnswer = isMemberLink
    ? !memberSubjectIsEmpty(memberSubjects[sessionSubjectId as string])
    : !!inputData;

  // Determine if we should show the choice screen
  const showChoiceScreen = canReuse && !disabled && !hasOwnAnswer && !hasDecided && !reuseError;

  // Declaration-only checkbox → per-category declare.
  const handleToggle = async (typeId: string, checked: boolean, subjectId?: string) => {
    if (subjectId) {
      setMemberDeclarationOverrides((prev) => ({
        ...prev,
        [subjectId]: { ...(prev[subjectId] ?? {}), [typeId]: checked },
      }));
    } else {
      setDeclarationOverrides((prev) => ({ ...prev, [typeId]: checked }));
    }
    try {
      await onDeclare({ [typeId]: checked }, subjectId);
    } catch (error) {
      log.error(
        { err: error, productTypeId: typeId, subjectId },
        "failed to declare product experience",
      );
      toast.error(t.declareFailed);
    } finally {
      if (subjectId) {
        setMemberDeclarationOverrides((prev) => {
          const memberMap = { ...(prev[subjectId] ?? {}) };
          delete memberMap[typeId];
          return { ...prev, [subjectId]: memberMap };
        });
      } else {
        setDeclarationOverrides((prev) => {
          const { [typeId]: _dropped, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleStartQuiz = async (productType: ProductType, subjectId?: string) => {
    if (!onStartQuiz) return;
    setStartingKey(startingKeyFor(productType.id, subjectId));
    setQuizStartError(null);
    try {
      // Idempotent while an attempt is in flight — reopening resumes the same questions.
      const attempt = await onStartQuiz(productType.id, subjectId);
      setActiveQuiz({
        productTypeId: productType.id,
        subjectId: subjectId ?? null,
        label: localize(productType.nameI18n),
        attempt,
      });
    } catch (error) {
      log.error({ err: error, subjectId }, "failed to start quiz attempt");
      setQuizStartError(t.quizStartFailed);
    } finally {
      setStartingKey(null);
    }
  };

  const openQuestionnaire = (
    productType: ProductType,
    assessment: ProductTypeAssessmentPublic,
    subjectId?: string,
  ) =>
    setActiveQuestionnaire({
      productTypeId: productType.id,
      subjectId: subjectId ?? null,
      label: localize(productType.nameI18n),
      assessment,
    });

  const handleUsePrevious = async () => {
    setIsApplyingPrevious(true);
    setReuseError(null);
    try {
      if (onApply) {
        // The backend applies and recomputes (incl. missing-type defaulting, questionnaire
        // answers and carried-over quiz states). No local priming — the value flows back via
        // `inputData`.
        await onApply();
        setHasDecided(true);
      }
    } catch (err) {
      log.error({ err }, "failed to apply previous product assessment answer");
      // Refusal (e.g. instance completed in another tab) — hide the button + show message.
      setReuseError(t.reuseNoLongerAvailable);
    } finally {
      setIsApplyingPrevious(false);
    }
  };

  const handleStartFresh = () => {
    setHasDecided(true);
  };

  const formatDate = (iso: string) => formatRegionalDate(parseDbTimestamp(iso));

  // Get container class based on style
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-4";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  const renderVerdictBadge = (verdict: "passed" | "failed" | "incomplete") => {
    if (verdict === "passed")
      return (
        <Badge variant="success" className="whitespace-nowrap">
          {t.verdictPassed}
        </Badge>
      );
    if (verdict === "failed") {
      return (
        <Badge variant="destructive" className="whitespace-nowrap">
          {t.verdictFailed}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="whitespace-nowrap">
        {t.verdictIncomplete}
      </Badge>
    );
  };

  // QL-653 — members mode: an unfinished type says how far the answering lines are
  // ("2 of 3 completed") instead of a bare "Incomplete" once some, but not all, have
  // completed it. Counts only, from the server's `memberSummary`.
  const renderMembersVerdictBadge = (
    verdict: "passed" | "failed" | "incomplete",
    summary: { total: number; completed?: number } | undefined,
  ) => {
    const completed = summary?.completed;
    if (
      verdict === "incomplete" &&
      completed !== undefined &&
      completed > 0 &&
      completed < summary!.total
    ) {
      return (
        <Badge variant="secondary" className="whitespace-nowrap">
          {t.typeCompletedCount(completed, summary!.total)}
        </Badge>
      );
    }
    return renderVerdictBadge(verdict);
  };

  const banner = resolveAttributionBanner(attribution, undefined);

  if (productTypes.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center border border-dashed rounded-lg p-8">
        {t.noProductTypesConfigured}
      </div>
    );
  }

  // Choice screen - show before form
  if (showChoiceScreen) {
    return (
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {/* Choice UI */}
        <div className="space-y-4">
          <div className="text-center py-4">
            <History className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-medium text-lg mb-2">{t.previousAnswerFound}</h3>
            <p className="text-sm text-muted-foreground">
              {t.youAnsweredOn} {formatDate(reuseDate ?? "")}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleUsePrevious} disabled={isApplyingPrevious} className="gap-2">
              {isApplyingPrevious ? t.applying : t.usePreviousAnswer}
            </Button>
            <Button
              variant="outline"
              onClick={handleStartFresh}
              disabled={isApplyingPrevious}
              className="gap-2"
            >
              {t.startFresh}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}
      {reuseError && <p className="text-sm text-destructive">{reuseError}</p>}
      {quizStartError && <p className="text-sm text-destructive">{quizStartError}</p>}
      {banner && (
        <RepresentativeNote
          text={representativeNoteText(
            t.repNote,
            banner,
            t.repGeneric,
            t.locale,
            t.repNoteViaMember,
          )}
        />
      )}
      {isRepresentativeList && <RepresentativeNote text={t.repListNote} />}

      {/* Product Type Rows */}
      <div className="space-y-3">
        {productTypes.map((type) => {
          const modes = assessmentsByType.get(type.id) ?? [];
          const typeLabel = localize(type.nameI18n);
          const aggEntry = calcResult?.experiences.find((e) => e.productTypeId === type.id);

          // No assessment configured — legacy yes/no experience checkbox.
          if (modes.length === 0) {
            if (isMembersMode) {
              const notPassedInUse = !(aggEntry?.hasExperience ?? false) && inUseSet.has(type.id);
              const notPassedNames = notPassedInUse
                ? members
                    .filter(
                      (m) =>
                        !(
                          m.experiences.find((e) => e.productTypeId === type.id)?.hasExperience ??
                          false
                        ),
                    )
                    .map((m) => m.name)
                : [];
              const warning = notPassedInUse
                ? notPassedNames.length > 0
                  ? t.notSuitableWarningMembers(notPassedNames.join(", "))
                  : t.notSuitableWarning
                : null;

              return (
                <ProductTypeBlock
                  key={type.id}
                  typeLabel={typeLabel}
                  representativeList={isRepresentativeList}
                  formatDate={formatDate}
                  inUseWarning={warning}
                  declarationGroup={{
                    memberSummary: aggEntry?.memberSummary,
                    expanded: isMemberLink || expandedRows.has(declarationRowKey(type.id)),
                    onToggleExpand: () => toggleExpand(declarationRowKey(type.id)),
                    members: onlyMemberLink(
                      members.map((m) => {
                        const stored = memberSubjects[m.subjectId];
                        // QL-660 — the server's counted answer first: a declaration an
                        // earlier POA gave no longer counts (the stored blob keeps it).
                        const storedChecked =
                          m.experiences.find((e) => e.productTypeId === type.id)?.hasExperience ??
                          stored?.experiences.find((e) => e.productTypeId === type.id)
                            ?.hasExperience ??
                          false;
                        const checked =
                          memberDeclarationOverrides[m.subjectId]?.[type.id] ?? storedChecked;
                        return {
                          subjectId: m.subjectId,
                          name: m.name,
                          memberRepresentative: m.memberRepresentative,
                          declaredBy: m.experiences.find((e) => e.productTypeId === type.id)
                            ?.declaredBy,
                          checked,
                          actionable: isMemberActionable(m.subjectId),
                          onToggle: (nextChecked: boolean) =>
                            handleToggle(type.id, nextChecked, m.subjectId),
                        };
                      }),
                    ),
                  }}
                />
              );
            }

            // "Not passed" here means no experience declared while the portfolio holds
            // products of this category.
            const hasExperience = declarationOverrides[type.id] ?? experienceMap[type.id] ?? false;
            const notPassedInUse = !hasExperience && inUseSet.has(type.id);
            return (
              <ProductTypeBlock
                key={type.id}
                typeLabel={typeLabel}
                representativeList={isRepresentativeList}
                formatDate={formatDate}
                inUseWarning={notPassedInUse ? t.notSuitableWarning : null}
                declarationSingle={{
                  checked: hasExperience,
                  actionable: !disabled,
                  onToggle: (checked) => handleToggle(type.id, checked),
                }}
              />
            );
          }

          // Assessment path: one block per product type — header with the overall
          // (AND) verdict badge, then one compact launcher sub-row per enabled mode.
          if (isMembersMode) {
            const verdict = aggEntry?.verdict ?? "incomplete";
            const notPassedInUse = verdict !== "passed" && inUseSet.has(type.id);
            const notPassedNames = notPassedInUse
              ? members
                  .filter(
                    (m) =>
                      m.experiences.find((e) => e.productTypeId === type.id)?.verdict !== "passed",
                  )
                  .map((m) => m.name)
              : [];
            const warning = notPassedInUse
              ? notPassedNames.length > 0
                ? t.notSuitableWarningMembers(notPassedNames.join(", "))
                : t.notSuitableWarning
              : null;

            const rows: ProductTypeBlockRow[] = modes.map((assessment) => {
              const outcome = aggEntry?.assessments?.find((o) => o.mode === assessment.mode);
              const memberLines = members.map((m) => {
                const memberEntry = m.experiences.find((e) => e.productTypeId === type.id);
                const stored = memberSubjects[m.subjectId];
                const memberQuizState = stored?.quizStates?.[type.id] ?? null;
                const memberAnswers = stored?.questionnaireAnswers?.[type.id] ?? {};
                const external = externalFor(
                  memberEntry ? [memberEntry] : undefined,
                  type.id,
                  assessment.mode,
                );
                const key = startingKeyFor(type.id, m.subjectId);
                const questions = assessment.questions ?? [];
                const hasAnswers = questions.some((q) => memberAnswers[q.id]);
                const questionnaireOutcome = findOutcome(
                  memberEntry ? [memberEntry] : undefined,
                  type.id,
                  "QUESTIONNAIRE",
                );
                return {
                  subjectId: m.subjectId,
                  name: m.name,
                  memberRepresentative: m.memberRepresentative,
                  // The counted outcome's record (D8): who the line names.
                  answeredBy: findOutcome(
                    memberEntry ? [memberEntry] : undefined,
                    type.id,
                    assessment.mode,
                  )?.answeredBy,
                  quizState: memberQuizState,
                  questionnaireAnswers: memberAnswers,
                  external,
                  questionnaireOutcome,
                  actionable: isMemberActionable(m.subjectId),
                  isStarting: startingKey === key,
                  anyStarting: startingKey !== null,
                  hasAnswers,
                  onStartQuiz: () => handleStartQuiz(type, m.subjectId),
                  onOpenQuestionnaire: () => openQuestionnaire(type, assessment, m.subjectId),
                };
              });
              return {
                key: assessment.id,
                mode: assessment.mode,
                assessment,
                group: {
                  status: outcome?.status ?? "incomplete",
                  // A member session: the viewer's OWN result for this mode (the count beside it
                  // says how the group stands), instead of the group's bare aggregate.
                  ownStatus: isMemberLink
                    ? findOutcome(
                        members
                          .find((m) => m.subjectId === sessionSubjectId)
                          ?.experiences.filter((e) => e.productTypeId === type.id),
                        type.id,
                        assessment.mode,
                      )?.status
                    : undefined,
                  memberSummary: outcome?.memberSummary,
                  expanded: isMemberLink || expandedRows.has(modeRowKey(type.id, assessment.mode)),
                  onToggleExpand: () => toggleExpand(modeRowKey(type.id, assessment.mode)),
                  members: onlyMemberLink(memberLines),
                },
              };
            });

            return (
              <ProductTypeBlock
                key={type.id}
                typeLabel={typeLabel}
                representativeList={isRepresentativeList}
                formatDate={formatDate}
                inUseWarning={warning}
                verdictBadge={renderMembersVerdictBadge(verdict, aggEntry?.memberSummary)}
                rows={rows}
              />
            );
          }

          const verdict = resolveSubjectVerdict(
            modes,
            calcResult?.experiences,
            type.id,
            quizStates,
          );
          const notPassedInUse = verdict !== "passed" && inUseSet.has(type.id);
          const rows: ProductTypeBlockRow[] = modes.map((assessment) => {
            const external = externalFor(calcResult?.experiences, type.id, assessment.mode);
            const key = startingKeyFor(type.id);
            const answers = questionnaireAnswers[type.id] ?? {};
            const questions = assessment.questions ?? [];
            const hasAnswers = questions.some((q) => answers[q.id]);
            const questionnaireOutcome = findOutcome(
              calcResult?.experiences,
              type.id,
              "QUESTIONNAIRE",
            );
            return {
              key: assessment.id,
              mode: assessment.mode,
              assessment,
              single: {
                quizState: quizStates[type.id] ?? null,
                questionnaireAnswers: answers,
                external,
                questionnaireOutcome,
                actionable: !disabled,
                isStarting: startingKey === key,
                anyStarting: startingKey !== null,
                hasAnswers,
                onStartQuiz: () => handleStartQuiz(type),
                onOpenQuestionnaire: () => openQuestionnaire(type, assessment),
              },
            };
          });

          return (
            <ProductTypeBlock
              key={type.id}
              typeLabel={typeLabel}
              representativeList={isRepresentativeList}
              formatDate={formatDate}
              inUseWarning={notPassedInUse ? t.notSuitableWarning : null}
              verdictBadge={renderVerdictBadge(verdict)}
              rows={rows}
            />
          );
        })}
      </div>

      {/* Quiz runner — mounted per run so its state resets on each open. */}
      {activeQuiz && onSubmitQuiz && (
        <ProductAssessmentQuizModal
          assessmentLabel={activeQuiz.label}
          attempt={activeQuiz.attempt}
          onSubmit={(answers) =>
            onSubmitQuiz(
              { productTypeId: activeQuiz.productTypeId, answers },
              activeQuiz.subjectId ?? undefined,
            )
          }
          onClose={() => setActiveQuiz(null)}
        />
      )}

      {/* Questionnaire runner — mounted per open so the draft re-seeds from the
          stored answers; one request on "Done", nothing while answering. */}
      {activeQuestionnaire && (
        <ProductAssessmentQuestionnaireModal
          assessmentLabel={activeQuestionnaire.label}
          questions={activeQuestionnaire.assessment.questions ?? []}
          initialAnswers={
            (activeQuestionnaire.subjectId
              ? memberSubjects[activeQuestionnaire.subjectId]?.questionnaireAnswers?.[
                  activeQuestionnaire.productTypeId
                ]
              : questionnaireAnswers[activeQuestionnaire.productTypeId]) ?? {}
          }
          disabled={
            activeQuestionnaire.subjectId
              ? !isMemberActionable(activeQuestionnaire.subjectId)
              : disabled
          }
          onSubmit={(answers) =>
            onSubmitQuestionnaire(
              { productTypeId: activeQuestionnaire.productTypeId, answers },
              activeQuestionnaire.subjectId ?? undefined,
            )
          }
          onClose={() => setActiveQuestionnaire(null)}
        />
      )}
    </div>
  );
}
